import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { getMessages } from "../api/newTelemetry";
import { ACCENT } from "../accent";
import { FRAMES, FRAME_IDS, guessFrame } from "./frames";
import VehicleDiagram from "./VehicleDiagram";

const RCOU_CHANNELS = Array.from({ length: 14 }, (_, i) => i + 1);
const MOVED_US = 20; // a channel that never moves this far isn't driving a motor

/**
 * ArduPilot logs ESC temperature in centi-degrees C on most builds, but some
 * report whole degrees. No log field states which, so we infer: nothing on a
 * drone runs at 250 °C, and nothing useful reads as 0.45 °C.
 *
 * ponytail: heuristic, not a unit read. Swap for the real multiplier if the API
 * ever exposes the log's field metadata.
 */
const normaliseTemp = (raw) => {
  if (raw == null || Number.isNaN(raw)) return null;
  return Math.abs(raw) > 250 ? raw / 100 : raw;
};

/** Nearest sample by time — ESC and RCOU are logged at different rates. */
const nearest = (rows, t) => {
  if (!rows?.length) return null;
  let lo = 0;
  let hi = rows.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (rows[mid].t < t) lo = mid + 1;
    else hi = mid;
  }
  const a = rows[lo];
  const b = rows[Math.max(0, lo - 1)];
  return Math.abs(a.t - t) <= Math.abs(b.t - t) ? a : b;
};

/** Modern logs carry one ESC type with an Instance column; older ones split it. */
const escSources = (messageIndex) => {
  const idx = messageIndex || {};
  const modern = idx.ESC?.fields || [];
  if (modern.includes("Instance")) return { mode: "instanced", types: ["ESC"], fields: modern };
  const legacy = Object.keys(idx)
    .filter((t) => /^ESC\d+$/.test(t))
    .sort((a, b) => Number(a.slice(3)) - Number(b.slice(3)));
  if (legacy.length) return { mode: "legacy", types: legacy, fields: idx[legacy[0]].fields || [] };
  return null;
};

const fmt = (v, digits = 0) => (v == null || Number.isNaN(v) ? "—" : v.toFixed(digits));

/**
 * Top-down vehicle view for a parsed log: a motor pad per rotor, scrubbed
 * across the flight.
 *
 * What is shown depends on what the log carries. ESC telemetry (RPM, temp)
 * only exists when bidirectional DShot or CAN ESCs were wired, so the panel
 * falls back to RCOU output PWM — which every log has — and says so rather
 * than drawing empty dials. The airframe itself is unknowable from the log
 * (it lives in FRAME_CLASS, which the API does not expose), hence the picker.
 */
const VehiclePanel = ({ log, fetchMessages = getMessages }) => {
  const theme = useTheme();
  const [frameId, setFrameId] = useState(() => guessFrame(log?.vehicle_type, 4));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(0);
  const userPickedFrame = useRef(false);

  const esc = useMemo(() => escSources(log?.message_index), [log]);
  const rcouFields = useMemo(() => {
    const have = log?.message_index?.RCOU?.fields || [];
    return RCOU_CHANNELS.map((c) => `C${c}`).filter((f) => have.includes(f));
  }, [log]);

  useEffect(() => {
    if (!log?.id || !rcouFields.length) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const escFields = esc ? ["Instance", "RPM", "Temp"].filter((f) => esc.fields.includes(f)) : [];

    Promise.all([
      fetchMessages(log.id, { type: "RCOU", fields: rcouFields, maxPoints: 600 }),
      ...(esc
        ? esc.types.map((t) =>
            fetchMessages(log.id, { type: t, fields: escFields, maxPoints: 2000 }).catch(() => null)
          )
        : []),
    ])
      .then(([rcou, ...escResults]) => {
        if (cancelled) return;
        const points = rcou.points || [];
        const t0 = points[0]?.TimeUS ?? 0;

        const samples = points.map((p) => {
          const ch = {};
          for (const c of RCOU_CHANNELS) if (p[`C${c}`] != null) ch[c] = p[`C${c}`];
          return { t: (p.TimeUS - t0) / 1e6, ch };
        });

        // Which outputs actually moved — the basis for the motor-count guess.
        const span = {};
        for (const c of RCOU_CHANNELS) {
          const vals = samples.map((s) => s.ch[c]).filter((v) => v != null);
          if (vals.length) span[c] = { min: Math.min(...vals), max: Math.max(...vals) };
        }
        const live = Object.entries(span)
          .filter(([, v]) => v.max - v.min > MOVED_US)
          .map(([c]) => Number(c));

        // ESC rows, keyed by 0-based instance.
        const byInstance = {};
        escResults.filter(Boolean).forEach((res, i) => {
          for (const p of res.points || []) {
            const inst = esc.mode === "instanced" ? p.Instance : i;
            if (inst == null) continue;
            (byInstance[inst] ??= []).push({
              t: (p.TimeUS - t0) / 1e6,
              rpm: p.RPM ?? null,
              temp: normaliseTemp(p.Temp),
            });
          }
        });
        for (const rows of Object.values(byInstance)) rows.sort((a, b) => a.t - b.t);

        const escCount = Object.keys(byInstance).length;
        if (!userPickedFrame.current) {
          setFrameId(guessFrame(log.vehicle_type, escCount || live.length || 4));
        }
        setData({ samples, span, byInstance, hasEsc: escCount > 0 });
        setCursor(Math.floor(samples.length / 2));
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [log, rcouFields, esc, fetchMessages]);

  const frame = FRAMES[frameId];

  const view = useMemo(() => {
    if (!data || !frame) return null;
    const sample = data.samples[Math.min(cursor, data.samples.length - 1)];
    if (!sample) return null;

    const lifters = frame.rotors.filter((r) => !r.forward);
    const lifterPwm = lifters.map((r) => sample.ch[r.ch]).filter((v) => v != null);
    const mean = lifterPwm.length ? lifterPwm.reduce((a, b) => a + b, 0) / lifterPwm.length : null;

    // Scale load and the imbalance threshold off this log's own observed range,
    // since MOT_PWM_MIN/MAX vary per airframe and aren't in the log.
    const mins = lifters.map((r) => data.span[r.ch]?.min).filter((v) => v != null);
    const maxes = lifters.map((r) => data.span[r.ch]?.max).filter((v) => v != null);
    const lo = mins.length ? Math.min(...mins) : 1000;
    const hi = maxes.length ? Math.max(...maxes) : 2000;
    const range = Math.max(1, hi - lo);
    const tol = Math.max(15, range * 0.04);

    const rotors = {};
    const rows = [];
    for (const r of frame.rotors) {
      const pwm = sample.ch[r.ch] ?? null;
      const e = nearest(data.byInstance[r.esc], sample.t);
      const dev = pwm != null && mean != null && !r.forward ? pwm - mean : null;
      const tone = dev == null ? "even" : dev > tol ? "high" : dev < -tol ? "low" : "even";
      const primary = data.hasEsc && e?.rpm != null ? Math.round(e.rpm) : pwm != null ? Math.round(pwm) : null;
      rotors[r.id] = {
        primary,
        tone,
        load: pwm != null ? Math.min(1, Math.max(0, (pwm - lo) / range)) : 0,
      };
      rows.push({ ...r, pwm, dev, tone, rpm: e?.rpm ?? null, temp: e?.temp ?? null });
    }

    const surfaces = {};
    for (const sf of frame.surfaces || []) {
      surfaces[sf.id] = { pwm: sample.ch[sf.ch] != null ? Math.round(sample.ch[sf.ch]) : null };
    }

    return { sample, rotors, rows, surfaces };
  }, [data, frame, cursor]);

  if (!rcouFields.length) {
    return (
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        This log has no RCOU output data, so motor activity can't be reconstructed.
      </Typography>
    );
  }
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
        <CircularProgress color="secondary" size={24} />
      </Box>
    );
  }
  if (error) {
    return (
      <Typography color="error" variant="body2">
        {error}
      </Typography>
    );
  }
  if (!view) {
    return (
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        No motor output samples in this log.
      </Typography>
    );
  }

  const toneColor = (tone) =>
    tone === "high" ? theme.palette.warning.main : tone === "low" ? ACCENT : theme.palette.text.primary;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Airframe</InputLabel>
          <Select
            label="Airframe"
            value={frameId}
            onChange={(e) => {
              userPickedFrame.current = true;
              setFrameId(e.target.value);
            }}
          >
            {FRAME_IDS.map((id) => (
              <MenuItem key={id} value={id}>
                {FRAMES[id].label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, maxWidth: 420 }}>
          {data.hasEsc
            ? "RPM and temperature from ESC telemetry."
            : "No ESC telemetry in this log — showing output PWM only."}{" "}
          Layout assumes the standard ArduPilot order for the selected frame.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(260px, 360px) 1fr" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.12 : 0.9),
          }}
        >
          <VehicleDiagram
            frame={frame}
            rotors={view.rotors}
            surfaces={view.surfaces}
            primaryUnit={data.hasEsc ? "rpm" : "µs"}
          />
        </Box>

        <Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${data.hasEsc ? 6 : 4}, minmax(0, 1fr))`,
              gap: 0.5,
              rowGap: 1,
              alignItems: "center",
            }}
          >
            {["Motor", "Out", "Δ mean", ...(data.hasEsc ? ["RPM", "Temp"] : []), "Spin"].map((h) => (
              <Typography
                key={h}
                variant="caption"
                sx={{ color: theme.palette.text.secondary, textTransform: "uppercase", letterSpacing: 0.4 }}
              >
                {h}
              </Typography>
            ))}
            {view.rows.map((r) => (
              <Box key={r.id} sx={{ display: "contents" }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  {r.id}
                  <Typography component="span" variant="caption" sx={{ ml: 0.5, color: theme.palette.text.secondary }}>
                    ch{r.ch}
                  </Typography>
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                  {fmt(r.pwm)}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", color: toneColor(r.tone) }}>
                  {r.dev == null ? "—" : `${r.dev > 0 ? "+" : ""}${fmt(r.dev)}`}
                </Typography>
                {data.hasEsc && (
                  <Typography variant="body2" sx={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                    {fmt(r.rpm)}
                  </Typography>
                )}
                {data.hasEsc && (
                  <Typography variant="body2" sx={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                    {r.temp == null ? "—" : `${fmt(r.temp, 1)}°`}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  {r.spin ? r.spin.toUpperCase() : "—"}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 3, px: 0.5 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              T+{view.sample.t.toFixed(1)}s
            </Typography>
            <Slider
              size="small"
              min={0}
              max={Math.max(0, data.samples.length - 1)}
              value={Math.min(cursor, data.samples.length - 1)}
              onChange={(_, v) => setCursor(v)}
              aria-label="Flight time"
              valueLabelDisplay="auto"
              valueLabelFormat={(i) => `${(data.samples[i]?.t ?? 0).toFixed(1)}s`}
              sx={{ color: ACCENT }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VehiclePanel;
